/* Meridian — bank oversight console (sandbox).
   The seven-step walkthrough a sponsor bank asks to see:
   entity → policy decision → account → ledger posting → three-way reconciliation
   → kill switch → exam pack. */
import { useCallback, useEffect, useMemo, useState } from "react";

import { api, money, shortId } from "./api";
import { Badge, Btn, Code, Kv, Panel, Stat, T, Table, styles } from "./ui";

const NAV = [
  ["overview", "Overview"],
  ["walkthrough", "Bank walkthrough"],
  ["entities", "Entities"],
  ["policy", "Policy centre"],
  ["ledger", "Ledger"],
  ["reconciliation", "Reconciliation"],
  ["capital", "Capital"],
  ["audit", "Audit trail"],
  ["exam", "Exam pack"],
  ["verticals", "Vertical packs"],
];

export default function MeridianConsole() {
  const [view, setView] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [entities, setEntities] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const id = "meridian-inter";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id; link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,400;14..32,500;14..32,600&display=swap";
      document.head.append(link);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [ov, ents, exc] = await Promise.all([api.overview(), api.entities(), api.reconExceptions()]);
      setOverview(ov); setEntities(ents.entities); setExceptions(exc.exceptions); setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await refresh(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const ks = overview?.kill_switch;

  return (
    <div className="mrd-root" data-testid="meridian-console">
      <style>{styles}</style>

      <header className="mrd-top">
        <div className="mrd-brand">
          <div className="mrd-mark" />
          <div className="mrd-brand-name">Meridian</div>
          <div className="mrd-brand-sub">financial trust &amp; control plane</div>
        </div>
        <div className="mrd-top-right">
          {overview && <span className="mrd-brand-sub" style={{ borderLeft: "none", paddingLeft: 0 }}>
            {overview.platform.name} · {overview.platform.vertical.replace("_", " ")}
          </span>}
          <Badge tone="warn" testId="env-badge">sandbox</Badge>
          {ks && <Badge tone={ks.engaged ? "critical" : "clean"} testId="kill-switch-badge">
            {ks.engaged ? "money movement halted" : "rails live"}
          </Badge>}
          <Btn size="sm" onClick={() => run(api.reset)} disabled={busy} testId="reset-demo-btn">Reset demo data</Btn>
        </div>
      </header>

      <div className="mrd-layout">
        <nav className="mrd-rail" data-testid="meridian-rail">
          <div className="mrd-rail-label">Console</div>
          {NAV.map(([key, label]) => (
            <button key={key} className={`mrd-rail-item ${view === key ? "is-active" : ""}`}
                    onClick={() => setView(key)} data-testid={`nav-${key}`}>
              <span>{label}</span>
              {key === "reconciliation" && exceptions.length > 0 &&
                <span className="mrd-rail-count">{exceptions.length}</span>}
              {key === "entities" && <span className="mrd-rail-count">{entities.length}</span>}
            </button>
          ))}
          <div className="mrd-rail-label">Rails</div>
          <div style={{ padding: "0 10px", fontSize: 12, color: T.dim2, lineHeight: 1.6 }}>
            {overview?.platform.rails_provider || "—"}<br />
            <span style={{ fontFamily: T.mono, fontSize: 10.5 }}>mock adapter · drop-in for live</span>
          </div>
        </nav>

        <main className="mrd-main">
          {error && (
            <div className="mrd-banner" style={{ borderColor: "rgba(255,107,94,0.5)", background: "rgba(255,107,94,0.10)", color: T.crit }}
                 data-testid="error-banner">
              {error}
            </div>
          )}

          {view === "overview" && <Overview overview={overview} exceptions={exceptions} onRun={run} busy={busy} />}
          {view === "walkthrough" && <Walkthrough onDone={refresh} />}
          {view === "entities" && <Entities entities={entities} />}
          {view === "policy" && <PolicyCentre entities={entities} />}
          {view === "ledger" && <Ledger />}
          {view === "reconciliation" && <Reconciliation exceptions={exceptions} onRun={run} busy={busy} />}
          {view === "capital" && <Capital entities={entities} />}
          {view === "audit" && <AuditTrail />}
          {view === "exam" && <ExamPacks />}
          {view === "verticals" && <Verticals />}
        </main>
      </div>
    </div>
  );
}

/* ───────────────────────── overview ───────────────────────── */

function Overview({ overview, exceptions, onRun, busy }) {
  if (!overview) return <Panel title="Loading">Fetching platform state…</Panel>;
  const tb = overview.trial_balance;
  const accounts = Object.entries(overview.accounts).filter(([, a]) => a.purpose !== "control");
  const critical = exceptions.filter((e) => e.severity === "critical").length;

  return (
    <>
      <div>
        <h1 className="mrd-h1">Program overview</h1>
        <p className="mrd-sub">
          The partner bank is the system of record for funds. Meridian holds a derived double-entry
          sub-ledger, reconciled continuously against bank-reported activity and vertical obligations.
        </p>
      </div>

      <div className="mrd-grid mrd-grid-4">
        <Stat label="Businesses" value={overview.entity_count}
              sub={Object.entries(overview.entities_by_status).map(([k, v]) => `${v} ${k}`).join(" · ")}
              testId="stat-entities" />
        <Stat label="Ledger balanced" value={tb.balanced ? "Yes" : "No"} tone={tb.balanced ? "clean" : "critical"}
              sub={`${money(tb.debits_cents)} debits · ${money(tb.credits_cents)} credits`} testId="stat-trial-balance" />
        <Stat label="Open exceptions" value={exceptions.length} tone={critical ? "critical" : "clean"}
              sub={`${critical} critical`} testId="stat-exceptions" />
        <Stat label="Audit records" value={overview.audit_records} sub="hash-chained, append-only"
              testId="stat-audit" />
      </div>

      <Panel title="Bank accounts and allocation buckets"
             meta="Balances are derived from postings; buckets are what the vertical rules require to be segregated."
             testId="panel-accounts">
        <Table testId="accounts-table"
          columns={[
            { key: "purpose", label: "Purpose", render: (r) => <Badge tone={r.purpose === "trust" ? "open" : "standard"}>{r.purpose}</Badge> },
            { key: "id", label: "Account", mono: true, render: (r) => shortId(r.id) },
            { key: "balance", label: "Balance", align: "right", render: (r) => money(r.balance_cents) },
            { key: "buckets", label: "Buckets", render: (r) => (
              <span style={{ fontFamily: T.mono, fontSize: 12 }}>
                {Object.entries(r.buckets).filter(([, v]) => v !== 0)
                  .map(([k, v]) => `${k}: ${money(v)}`).join("   ") || "—"}
              </span>) },
          ]}
          rows={accounts.map(([id, a]) => ({ __key: id, id, ...a }))} />
      </Panel>

      <div className="mrd-grid mrd-grid-2">
        <Panel title="Last reconciliation" testId="panel-last-recon"
               actions={<Btn size="sm" onClick={() => onRun(() => api.runRecon())} disabled={busy} testId="run-recon-quick">Run now</Btn>}>
          {!overview.last_reconciliation ? <div style={{ color: T.dim2, fontSize: 13 }}>No run yet.</div> : (
            <div className="mrd-grid mrd-grid-2" style={{ gap: 14 }}>
              <Kv label="Status" value={<Badge tone={overview.last_reconciliation.status}>{overview.last_reconciliation.status}</Badge>} />
              <Kv label="Exceptions" value={overview.last_reconciliation.exception_count} />
              <Kv label="Bank leg" value={money(overview.last_reconciliation.summary.three_way.bank_cents)} />
              <Kv label="Ledger leg" value={money(overview.last_reconciliation.summary.three_way.ledger_cents)} />
              <Kv label="Obligations leg" value={money(overview.last_reconciliation.summary.three_way.obligations_cents)} />
              <Kv label="Legs agree" value={<Badge tone={overview.last_reconciliation.summary.legs_agree ? "clean" : "critical"}>
                {String(overview.last_reconciliation.summary.legs_agree)}</Badge>} />
            </div>
          )}
        </Panel>

        <Panel title="Controls" meta="Fail-closed: while the switch is engaged no disbursement can post."
               testId="panel-controls">
          <div className="mrd-grid" style={{ gap: 14 }}>
            <Kv label="Active policy"
                value={overview.active_policy ? `${overview.active_policy.name} · v${overview.active_policy.version} · ${overview.active_policy.rules} rules` : "none"} />
            <Kv label="Kill switch" value={<Badge tone={overview.kill_switch.engaged ? "critical" : "clean"}>
              {overview.kill_switch.engaged ? "engaged" : "released"}</Badge>} />
            <div style={{ display: "flex", gap: 8 }}>
              <Btn tone={overview.kill_switch.engaged ? "default" : "danger"} size="sm" disabled={busy}
                   testId="toggle-kill-switch"
                   onClick={() => onRun(() => api.setKillSwitch({
                     engaged: !overview.kill_switch.engaged,
                     reason: overview.kill_switch.engaged ? "released from console" : "engaged from console",
                     actor: "bank_oversight",
                   }))}>
                {overview.kill_switch.engaged ? "Release money movement" : "Halt money movement"}
              </Btn>
              <Btn size="sm" disabled={busy} testId="run-drill"
                   onClick={() => onRun(async () => { window.__drill = await api.drill(); })}>
                Run reconstruction drill
              </Btn>
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
}

/* ───────────────────────── walkthrough ───────────────────────── */

const STEP_LABELS = [
  "Onboard a business and capture evidence",
  "Evaluate the disbursement against policy",
  "Open a bank account at the rails provider",
  "Post a balanced double-entry ledger movement",
  "Run three-way reconciliation",
  "Engage the kill switch and prove it fails closed",
  "Generate the exam pack",
];

function Walkthrough({ onDone }) {
  const [steps, setSteps] = useState([]);
  const [running, setRunning] = useState(false);

  const push = (title, detail, tone = "clean") =>
    setSteps((s) => [...s, { title, detail, tone }]);

  const runAll = async () => {
    setRunning(true); setSteps([]);
    try {
      await api.reset();
      const { entities } = await api.entities();
      const target = entities.find((e) => e.status === "verified") || entities[0];
      push(STEP_LABELS[0], `${target.legal_name} · rails entity ${target.rails_entity_id} · status ${target.status}`);

      const blocked = await api.simulate({ entity_id: target.id, action: "disburse", bucket: "trust",
                                           amount_cents: 500000 });
      push(STEP_LABELS[1], `unbacked trust withdrawal → ${blocked.result.outcome} [${blocked.result.reason_codes.join(", ")}] rule=${blocked.result.matched_rule}`,
           "critical");

      const accounts = (await api.accounts()).accounts.filter((a) => a.entity_id === target.id);
      const trust = accounts.find((a) => a.purpose === "trust");
      const operating = accounts.find((a) => a.purpose === "operating");
      push(STEP_LABELS[2], `trust ${trust.routing_number} ····${trust.account_number_last4} · operating ····${operating.account_number_last4}`);

      const ob = (await api.entity(target.id)).obligations
        .find((o) => o.status === "open" && o.bucket === "trust" && o.amount_cents > 125000);
      const moved = await api.transfer({ entity_id: target.id, from_account_id: trust.id,
        to_account_id: operating.id, amount_cents: 125000, bucket: "trust",
        obligation_id: ob?.id, description: "Owner distribution (walkthrough)" });
      push(STEP_LABELS[3], moved.moved
        ? `${moved.decision.outcome} [${moved.decision.reason_codes.join(", ")}] · entry ${shortId(moved.journal_entry_id)} · rails ${moved.rails_transfer_id}`
        : `refused: ${moved.decision.reason_codes.join(", ")}`, moved.moved ? "clean" : "critical");

      const recon = await api.runRecon();
      const summary = recon.runs.map((r) => `${r.entity.split(" ")[0]}: ${r.status} (${r.exception_count})`).join(" · ");
      push(STEP_LABELS[4], summary, recon.runs.some((r) => r.exception_count) ? "warn" : "clean");

      await api.setKillSwitch({ engaged: true, reason: "walkthrough drill", actor: "bank_oversight" });
      const refused = await api.transfer({ entity_id: target.id, from_account_id: trust.id,
        to_account_id: operating.id, amount_cents: 1000, bucket: "trust",
        obligation_id: ob?.id, description: "should be refused" });
      await api.setKillSwitch({ engaged: false, reason: "walkthrough complete", actor: "bank_oversight" });
      push(STEP_LABELS[5], `transfer while halted → moved=${refused.moved} · ${refused.decision.outcome} [${refused.decision.reason_codes.join(", ")}]`,
           refused.moved ? "critical" : "clean");

      const pack = await api.generateExamPack({ period_days: 90 });
      push(STEP_LABELS[6], `sha256 ${pack.exam_pack.sha256.slice(0, 24)}… · ${JSON.stringify(pack.exam_pack.counts)} · chain ok=${pack.exam_pack.audit_chain.ok}`);
      await onDone();
    } catch (e) {
      push("Walkthrough failed", e.message, "critical");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <div>
        <h1 className="mrd-h1">Bank walkthrough</h1>
        <p className="mrd-sub">
          Runs the full control path end to end against the sandbox rails adapter: onboarding evidence,
          a policy refusal with reason codes, account opening, a balanced posting, three-way reconciliation,
          the kill switch failing closed, and a hash-verified exam pack.
        </p>
      </div>
      <Panel title="Seven steps" meta="Resets demo data, then executes each step in order."
             actions={<Btn tone="primary" onClick={runAll} disabled={running} testId="run-walkthrough">
               {running ? "Running…" : "Run walkthrough"}</Btn>}
             testId="panel-walkthrough">
        {steps.length === 0 && <div style={{ color: T.dim2, fontSize: 13 }}>No run yet.</div>}
        <div className="mrd-steps" data-testid="walkthrough-steps">
          {steps.map((s, i) => (
            <div className="mrd-step" key={i} data-testid={`walkthrough-step-${i + 1}`}>
              <div className="mrd-step-n">{String(i + 1).padStart(2, "0")}</div>
              <div>
                <div className="mrd-step-title">{s.title}</div>
                <div className="mrd-step-detail">{s.detail}</div>
              </div>
              <Badge tone={s.tone === "warn" ? "medium" : s.tone}>{s.tone === "critical" ? "enforced" : s.tone === "warn" ? "exceptions" : "pass"}</Badge>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );
}

/* ───────────────────────── entities ───────────────────────── */

function Entities({ entities }) {
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    api.entity(selected).then(setDetail).catch(() => setDetail(null));
  }, [selected]);

  return (
    <>
      <div>
        <h1 className="mrd-h1">Businesses</h1>
        <p className="mrd-sub">Each business is onboarded through the rails provider with hash-addressed evidence and a
          certification that expires — no permanent approvals.</p>
      </div>

      <Panel title="Roster" testId="panel-entities">
        <Table testId="entities-table"
          columns={[
            { key: "legal_name", label: "Legal name" },
            { key: "state", label: "State" },
            { key: "status", label: "Status", render: (r) => <Badge tone={r.status}>{r.status}</Badge> },
            { key: "risk_tier", label: "Risk", render: (r) => <Badge tone={r.risk_tier}>{r.risk_tier}</Badge> },
            { key: "rails_entity_id", label: "Rails entity", mono: true },
            { key: "units", label: "Units", align: "right", render: (r) => r.meta?.unit_count ?? "—" },
            { key: "action", label: "", align: "right", render: (r) => (
              <Btn size="sm" onClick={() => setSelected(r.id)} testId={`view-entity-${r.id.slice(0, 8)}`}>Open</Btn>) },
          ]}
          rows={entities.map((e) => ({ ...e, __key: e.id }))} />
      </Panel>

      {detail && (
        <>
          <Panel title={detail.entity.legal_name} meta={`${detail.entity.entity_type.toUpperCase()} · EIN ····${detail.entity.ein_last4} · cert expires ${detail.entity.certification_expires_at?.slice(0, 10)}`}
                 actions={<Btn size="sm" onClick={() => setSelected(null)} testId="close-entity">Close</Btn>}
                 testId="panel-entity-detail">
            <div className="mrd-grid mrd-grid-4" style={{ gap: 14 }}>
              {Object.entries(detail.facts).map(([k, v]) => (
                <Kv key={k} label={k.replace(/_/g, " ")} value={typeof v === "boolean"
                  ? <Badge tone={v ? "clean" : "critical"}>{String(v)}</Badge> : String(v ?? "—")} />
              ))}
            </div>
          </Panel>

          <div className="mrd-grid mrd-grid-2">
            <Panel title="Evidence" meta="SHA-256 of every document captured" testId="panel-evidence">
              <Table testId="evidence-table"
                columns={[
                  { key: "kind", label: "Kind" },
                  { key: "label", label: "Document" },
                  { key: "sha256", label: "SHA-256", mono: true, render: (r) => `${r.sha256.slice(0, 16)}…` },
                ]} rows={detail.evidence} />
            </Panel>
            <Panel title="Beneficial owners" testId="panel-ubos">
              <Table testId="ubos-table"
                columns={[
                  { key: "full_name", label: "Name" },
                  { key: "ownership_pct", label: "Ownership", align: "right", render: (r) => `${r.ownership_pct}%` },
                  { key: "verified", label: "Verified", render: (r) => <Badge tone={r.verified ? "clean" : "critical"}>{String(r.verified)}</Badge> },
                ]} rows={detail.ubos} />
            </Panel>
          </div>

          <Panel title="Obligations" meta="The third leg of reconciliation: what the vertical says is owed."
                 testId="panel-obligations">
            <Table testId="obligations-table"
              columns={[
                { key: "kind", label: "Kind" },
                { key: "external_ref", label: "Reference", mono: true },
                { key: "counterparty", label: "Counterparty" },
                { key: "bucket", label: "Bucket" },
                { key: "amount_cents", label: "Amount", align: "right", render: (r) => money(r.amount_cents) },
                { key: "status", label: "Status", render: (r) => <Badge tone={r.status}>{r.status}</Badge> },
              ]} rows={detail.obligations} />
          </Panel>
        </>
      )}
    </>
  );
}

/* ───────────────────────── policy ───────────────────────── */

function PolicyCentre({ entities }) {
  const [policies, setPolicies] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [form, setForm] = useState({ entity_id: "", action: "disburse", bucket: "trust",
                                     amount_cents: 500000, has_obligation: false, crosses: false });
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const [p, d] = await Promise.all([api.policies(), api.decisions()]);
    setPolicies(p.policies); setDecisions(d.decisions);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (entities.length && !form.entity_id) setForm((f) => ({ ...f, entity_id: entities[0].id })); },
            [entities, form.entity_id]);

  const active = policies.find((p) => p.status === "active");

  const simulate = async () => {
    const r = await api.simulate({
      entity_id: form.entity_id, action: form.action, bucket: form.bucket,
      amount_cents: Number(form.amount_cents),
      obligation_ref: form.has_obligation ? "SIM-OB-1" : null,
      crosses_trust_boundary: form.crosses,
    });
    setResult(r); await load();
  };

  return (
    <>
      <div>
        <h1 className="mrd-h1">Policy centre</h1>
        <p className="mrd-sub">Versioned policy-as-code. Rules are evaluated in order, first match decides, and every
          evaluation is retained with its inputs and stable reason codes.</p>
      </div>

      <Panel title="Simulate a decision" meta="Simulations are recorded and flagged, never executed."
             actions={<Btn tone="primary" size="sm" onClick={simulate} testId="simulate-btn">Evaluate</Btn>}
             testId="panel-simulate">
        <div className="mrd-grid mrd-grid-4" style={{ gap: 14, alignItems: "end" }}>
          <div className="mrd-field">
            <label>Business</label>
            <select className="mrd-select" value={form.entity_id} data-testid="sim-entity"
                    onChange={(e) => setForm({ ...form, entity_id: e.target.value })}>
              {entities.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
          </div>
          <div className="mrd-field">
            <label>Action</label>
            <select className="mrd-select" value={form.action} data-testid="sim-action"
                    onChange={(e) => setForm({ ...form, action: e.target.value })}>
              {["disburse", "open_account", "collect"].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="mrd-field">
            <label>Bucket</label>
            <select className="mrd-select" value={form.bucket} data-testid="sim-bucket"
                    onChange={(e) => setForm({ ...form, bucket: e.target.value })}>
              {["trust", "security_deposit", "reserve", "operating"].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="mrd-field">
            <label>Amount (cents)</label>
            <input className="mrd-input" type="number" value={form.amount_cents} data-testid="sim-amount"
                   onChange={(e) => setForm({ ...form, amount_cents: e.target.value })} />
          </div>
          <label className="mrd-check">
            <input type="checkbox" checked={form.has_obligation} data-testid="sim-obligation"
                   onChange={(e) => setForm({ ...form, has_obligation: e.target.checked })} />
            obligation on file
          </label>
          <label className="mrd-check">
            <input type="checkbox" checked={form.crosses} data-testid="sim-crosses"
                   onChange={(e) => setForm({ ...form, crosses: e.target.checked })} />
            crosses trust boundary
          </label>
        </div>

        {result && (
          <div style={{ marginTop: 18 }} data-testid="sim-result">
            <div className="mrd-grid mrd-grid-3" style={{ gap: 14 }}>
              <Kv label="Outcome" value={<Badge tone={result.result.outcome}>{result.result.outcome}</Badge>} />
              <Kv label="Matched rule" value={result.result.matched_rule || "— default"} mono />
              <Kv label="Reason codes" value={result.result.reason_codes.join(", ")} mono />
            </div>
            {result.result.controls.length > 0 &&
              <div style={{ marginTop: 12 }}><Kv label="Required controls" value={result.result.controls.join(", ")} mono /></div>}
            <div style={{ marginTop: 14 }}><Code value={result.facts} testId="sim-facts" /></div>
          </div>
        )}
      </Panel>

      <Panel title={active ? `${active.name} · v${active.version}` : "No active policy"}
             meta="Ordered rules — first match wins" testId="panel-active-policy">
        <Table testId="policy-rules-table"
          columns={[
            { key: "id", label: "Rule", mono: true },
            { key: "outcome", label: "Outcome", render: (r) => <Badge tone={r.outcome}>{r.outcome}</Badge> },
            { key: "when", label: "Conditions", mono: true, render: (r) => (r.when || [])
                .map((c) => `${c.fact} ${c.op} ${JSON.stringify(c.value ?? "")}`).join("  ·  ") },
            { key: "reason_codes", label: "Reason codes", mono: true, render: (r) => (r.reason_codes || []).join(", ") },
            { key: "controls", label: "Controls", mono: true, render: (r) => (r.controls || []).join(", ") || "—" },
          ]}
          rows={(active?.rules || []).map((r) => ({ ...r, __key: r.id }))} />
      </Panel>

      <Panel title="Decision log" meta="Every evaluation, executed or simulated" testId="panel-decisions">
        <Table testId="decisions-table"
          columns={[
            { key: "created_at", label: "When", mono: true, render: (r) => r.created_at.slice(11, 19) },
            { key: "action", label: "Action" },
            { key: "outcome", label: "Outcome", render: (r) => <Badge tone={r.outcome}>{r.outcome}</Badge> },
            { key: "matched_rule", label: "Rule", mono: true, render: (r) => r.matched_rule || "default" },
            { key: "reason_codes", label: "Reason codes", mono: true, render: (r) => r.reason_codes.join(", ") },
            { key: "simulated", label: "Mode", render: (r) => <Badge tone={r.simulated ? "medium" : "clean"}>{r.simulated ? "simulated" : "executed"}</Badge> },
          ]}
          rows={decisions.map((d) => ({ ...d, __key: d.id }))} />
      </Panel>
    </>
  );
}

/* ───────────────────────── ledger ───────────────────────── */

function Ledger() {
  const [data, setData] = useState(null);
  const [bank, setBank] = useState([]);

  useEffect(() => {
    api.entries().then(setData);
    api.bankTransactions().then((r) => setBank(r.bank_transactions));
  }, []);

  const tb = data?.trial_balance;
  const unmatched = useMemo(() => bank.filter((t) => !t.matched_entry_id).length, [bank]);

  return (
    <>
      <div>
        <h1 className="mrd-h1">Ledger</h1>
        <p className="mrd-sub">Immutable double-entry postings. Corrections are reversing entries, never edits.</p>
      </div>

      <div className="mrd-grid mrd-grid-3">
        <Stat label="Trial balance" value={tb?.balanced ? "Balanced" : "Out of balance"}
              tone={tb?.balanced ? "clean" : "critical"} sub={tb ? money(tb.debits_cents) : "—"} testId="stat-ledger-tb" />
        <Stat label="Journal entries" value={data?.entries.length ?? "—"} testId="stat-entry-count" />
        <Stat label="Unmatched bank items" value={unmatched} tone={unmatched ? "critical" : "clean"}
              sub="no corresponding posting" testId="stat-unmatched" />
      </div>

      <Panel title="Journal entries" testId="panel-entries">
        <Table testId="entries-table"
          columns={[
            { key: "ref", label: "Reference", mono: true },
            { key: "event_type", label: "Event" },
            { key: "description", label: "Description" },
            { key: "lines", label: "Postings", mono: true, render: (r) => r.lines
                .map((l) => `${l.direction === "debit" ? "DR" : "CR"} ${l.bucket} ${money(l.amount_cents)}`).join("   ") },
            { key: "balanced", label: "Balanced", render: (r) => <Badge tone={r.balanced ? "clean" : "critical"}>{String(r.balanced)}</Badge> },
          ]}
          rows={(data?.entries || []).map((e) => ({ ...e, __key: e.id }))} />
      </Panel>

      <Panel title="Bank-reported activity" meta="Mirror of the rails provider; never edited by us." testId="panel-bank-tx">
        <Table testId="bank-tx-table"
          columns={[
            { key: "rails_tx_id", label: "Rails transaction", mono: true },
            { key: "description", label: "Description" },
            { key: "amount_cents", label: "Amount", align: "right", render: (r) => money(r.amount_cents) },
            { key: "posted_at", label: "Posted", mono: true, render: (r) => r.posted_at.slice(0, 16).replace("T", " ") },
            { key: "matched_entry_id", label: "Matched", render: (r) => <Badge tone={r.matched_entry_id ? "clean" : "critical"}>
                {r.matched_entry_id ? shortId(r.matched_entry_id) : "unmatched"}</Badge> },
          ]}
          rows={bank.map((t) => ({ ...t, __key: t.id }))} />
      </Panel>
    </>
  );
}

/* ───────────────────────── reconciliation ───────────────────────── */

function Reconciliation({ exceptions, onRun, busy }) {
  const [runs, setRuns] = useState([]);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => setRuns((await api.reconRuns()).runs), []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div>
        <h1 className="mrd-h1">Three-way reconciliation</h1>
        <p className="mrd-sub">
          Bank-reported activity, our derived sub-ledger and the vertical's obligations must agree.
          Trust shortfall and commingling are treated as critical: they are the failures that end programs.
        </p>
      </div>

      <Panel title="Runs" meta="One run per business per cycle"
             actions={<>
               <Btn size="sm" disabled={busy} testId="run-recon"
                    onClick={() => onRun(async () => { await api.runRecon(); await load(); })}>Run reconciliation</Btn>
               <Btn size="sm" disabled={busy} testId="run-drill-recon"
                    onClick={async () => setDrill(await api.drill())}>Reconstruction drill</Btn>
             </>}
             testId="panel-recon-runs">
        <Table testId="recon-runs-table"
          columns={[
            { key: "started_at", label: "Started", mono: true, render: (r) => r.started_at.slice(0, 19).replace("T", " ") },
            { key: "status", label: "Status", render: (r) => <Badge tone={r.status}>{r.status}</Badge> },
            { key: "bank", label: "Bank leg", align: "right", render: (r) => money(r.summary?.three_way?.bank_cents || 0) },
            { key: "ledger", label: "Ledger leg", align: "right", render: (r) => money(r.summary?.three_way?.ledger_cents || 0) },
            { key: "obligations", label: "Obligations leg", align: "right", render: (r) => money(r.summary?.three_way?.obligations_cents || 0) },
            { key: "matched_count", label: "Matched", align: "right" },
            { key: "exception_count", label: "Exceptions", align: "right", render: (r) => (
              <span style={{ color: r.exception_count ? T.crit : T.dim }}>{r.exception_count}</span>) },
          ]}
          rows={runs.map((r) => ({ ...r, __key: r.id }))} />
      </Panel>

      {drill && (
        <Panel title="Reconstruction drill" meta="Can the sub-ledger be rebuilt from bank data plus the event log alone?"
               testId="panel-drill">
          <div className="mrd-grid mrd-grid-4" style={{ gap: 14 }}>
            <Kv label="Engine result" value={<Badge tone={drill.engine_ok ? "clean" : "critical"}>
              {drill.engine_ok ? "every break attributed" : "unexplained residual"}</Badge>} />
            <Kv label="Accounts reproduced"
                value={`${drill.accounts_reproduced} / ${drill.accounts_total}`} mono />
            <Kv label="Unexplained residual"
                value={<span style={{ color: drill.unexplained_cents ? T.crit : T.ok }}>
                  {money(drill.unexplained_cents)}</span>} mono />
            <Kv label="Audit chain" value={<Badge tone={drill.audit_chain.ok ? "clean" : "critical"}>
              {drill.audit_chain.ok ? `verified · ${drill.audit_chain.records} records` : "broken"}</Badge>} />
          </div>
          <div style={{ marginTop: 14 }}>
            <Table testId="drill-table"
              columns={[
                { key: "entity", label: "Business" },
                { key: "purpose", label: "Account" },
                { key: "bank_cents", label: "Bank", align: "right", render: (r) => money(r.bank_cents) },
                { key: "rebuilt_ledger_cents", label: "Rebuilt ledger", align: "right", render: (r) => money(r.rebuilt_ledger_cents) },
                { key: "delta_cents", label: "Break", align: "right", render: (r) => (
                  <span style={{ color: r.delta_cents ? T.warn : T.dim2 }}>{r.delta_cents ? money(r.delta_cents) : "—"}</span>) },
                { key: "attributed_cents", label: "Attributed", align: "right", render: (r) => (
                  r.delta_cents ? money(r.attributed_cents) : "—") },
                { key: "unexplained_cents", label: "Unexplained", align: "right", render: (r) => (
                  <span style={{ color: r.unexplained_cents ? T.crit : T.ok }}>{money(r.unexplained_cents)}</span>) },
                { key: "explained", label: "Verdict", render: (r) => <Badge tone={r.explained ? "clean" : "critical"}>
                  {r.match ? "reproduced" : r.explained ? "break attributed" : "unexplained"}</Badge> },
              ]} rows={drill.accounts.map((a, i) => ({ ...a, __key: i }))} />
          </div>
          {drill.accounts.some((a) => a.unmatched_bank_items?.length > 0) && (
            <div style={{ marginTop: 18 }}>
              <div className="mrd-kv-label" style={{ marginBottom: 8 }}>
                Bank items carrying the break — no corresponding posting
              </div>
              <Table testId="drill-unmatched-table"
                columns={[
                  { key: "entity", label: "Business" },
                  { key: "rails_tx_id", label: "Rails transaction", mono: true },
                  { key: "description", label: "Description" },
                  { key: "amount_cents", label: "Amount", align: "right", render: (r) => money(r.amount_cents) },
                  { key: "posted_at", label: "Posted", mono: true, render: (r) => r.posted_at.slice(0, 16).replace("T", " ") },
                ]}
                rows={drill.accounts.flatMap((a) =>
                  (a.unmatched_bank_items || []).map((t, i) => ({ ...t, entity: a.entity, __key: `${a.account_id}-${i}` })))} />
            </div>
          )}
          <p className="mrd-sub" style={{ marginTop: 14 }}>{drill.statement}</p>
        </Panel>
      )}

      <Panel title="Open exceptions" meta="Each exception carries the evidence needed to work it" testId="panel-exceptions">
        <Table testId="exceptions-table" empty="No open exceptions."
          columns={[
            { key: "severity", label: "Severity", render: (r) => <Badge tone={r.severity}>{r.severity}</Badge> },
            { key: "kind", label: "Kind", mono: true },
            { key: "detail", label: "Detail", mono: true, render: (r) => JSON.stringify(r.detail) },
            { key: "action", label: "", align: "right", render: (r) => (
              <Btn size="sm" disabled={busy} testId={`resolve-${r.id.slice(0, 8)}`}
                   onClick={() => onRun(() => api.resolveException(r.id, { note: "worked in console", actor: "bank_oversight" }))}>
                Resolve
              </Btn>) },
          ]}
          rows={exceptions.map((e) => ({ ...e, __key: e.id }))} />
      </Panel>
    </>
  );
}

/* ───────────────────────── capital ───────────────────────── */

function Capital({ entities }) {
  const [entityId, setEntityId] = useState("");
  const [offers, setOffers] = useState([]);
  const [features, setFeatures] = useState(null);

  useEffect(() => { if (entities.length && !entityId) setEntityId(entities[0].id); }, [entities, entityId]);
  useEffect(() => { if (entityId) api.capitalOffers(entityId).then((r) => setOffers(r.offers)); }, [entityId]);

  const underwrite = async () => {
    const r = await api.underwrite({ entity_id: entityId });
    setFeatures(r.features_used);
    setOffers((await api.capitalOffers(entityId)).offers);
  };

  return (
    <>
      <div>
        <h1 className="mrd-h1">Capital</h1>
        <p className="mrd-sub">
          Offers are underwritten on vertical collateral — rent roll, approved pay apps, purchase-order history —
          not generic revenue. The provider behind this screen is a mock adapter: a live lender (or bank line)
          drops into the same interface without changing the API.
        </p>
      </div>

      <Panel title="Underwriting" meta="MOCKED provider · adapter-ready"
             actions={<>
               <select className="mrd-select" value={entityId} data-testid="capital-entity"
                       onChange={(e) => setEntityId(e.target.value)}>
                 {entities.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
               </select>
               <Btn tone="primary" size="sm" onClick={underwrite} testId="underwrite-btn">Underwrite</Btn>
             </>}
             testId="panel-capital">
        {features && <div style={{ marginBottom: 16 }}><Code value={features} testId="capital-features" /></div>}
        <Table testId="offers-table" empty="No offers — run underwriting."
          columns={[
            { key: "product", label: "Product" },
            { key: "max_amount_cents", label: "Limit", align: "right", render: (r) => money(r.max_amount_cents) },
            { key: "fee_bps", label: "Fee", align: "right", render: (r) => `${(r.fee_bps / 100).toFixed(2)}%` },
            { key: "term_days", label: "Term", align: "right", render: (r) => `${r.term_days}d` },
            { key: "features", label: "Basis", mono: true, render: (r) => JSON.stringify(r.features) },
            { key: "provider", label: "Provider", mono: true },
          ]}
          rows={offers.map((o) => ({ ...o, __key: o.id }))} />
      </Panel>
    </>
  );
}

/* ───────────────────────── audit ───────────────────────── */

function AuditTrail() {
  const [records, setRecords] = useState([]);
  const [chain, setChain] = useState(null);

  useEffect(() => { api.audit().then((r) => setRecords(r.records)); }, []);

  return (
    <>
      <div>
        <h1 className="mrd-h1">Audit trail</h1>
        <p className="mrd-sub">Append-only and hash-chained: each record commits to its predecessor, so tampering
          anywhere breaks verification everywhere after it.</p>
      </div>
      <Panel title="Records" meta={`${records.length} shown`}
             actions={<Btn size="sm" onClick={async () => setChain(await api.verifyAudit())} testId="verify-chain">
               Verify chain</Btn>}
             testId="panel-audit">
        {chain && (
          <div className="mrd-banner" data-testid="chain-result"
               style={{ marginBottom: 16,
                        borderColor: chain.ok ? "rgba(46,194,126,0.5)" : "rgba(255,107,94,0.5)",
                        background: chain.ok ? "rgba(46,194,126,0.10)" : "rgba(255,107,94,0.10)",
                        color: chain.ok ? T.ok : T.crit }}>
            {chain.ok ? `Chain verified across ${chain.records} records · head ${chain.head_hash.slice(0, 16)}…`
                      : `Chain broken at seq ${chain.broken_at_seq}`}
          </div>
        )}
        <Table testId="audit-table"
          columns={[
            { key: "seq", label: "Seq", mono: true },
            { key: "created_at", label: "When", mono: true, render: (r) => r.created_at.slice(0, 19).replace("T", " ") },
            { key: "action", label: "Action", mono: true },
            { key: "actor", label: "Actor" },
            { key: "prev_hash", label: "Prev", mono: true },
            { key: "hash", label: "Hash", mono: true },
            { key: "payload", label: "Payload", mono: true, render: (r) => JSON.stringify(r.payload).slice(0, 90) },
          ]}
          rows={records.map((r) => ({ ...r, __key: r.seq }))} />
      </Panel>
    </>
  );
}

/* ───────────────────────── exam pack ───────────────────────── */

function ExamPacks() {
  const [packs, setPacks] = useState([]);
  const [doc, setDoc] = useState(null);

  const load = useCallback(async () => setPacks((await api.examPacks()).exam_packs), []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => { await api.generateExamPack({ period_days: 90 }); await load(); };
  const open = async (id) => setDoc(await api.examPack(id));
  const download = () => {
    const blob = new Blob([JSON.stringify(doc.document, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `meridian-exam-pack-${doc.id.slice(0, 8)}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div>
        <h1 className="mrd-h1">Exam pack</h1>
        <p className="mrd-sub">One export containing entity roster with evidence hashes, policy versions in force,
          every decision with reason codes, reconciliation history, kill-switch history and a verified audit chain.</p>
      </div>
      <Panel title="Generated packs" meta="Each pack is content-hashed"
             actions={<Btn tone="primary" size="sm" onClick={generate} testId="generate-exam-pack">Generate (90 days)</Btn>}
             testId="panel-exam-packs">
        <Table testId="exam-packs-table" empty="No packs generated yet."
          columns={[
            { key: "generated_at", label: "Generated", mono: true, render: (r) => r.generated_at.slice(0, 19).replace("T", " ") },
            { key: "sha256", label: "SHA-256", mono: true, render: (r) => `${r.sha256.slice(0, 20)}…` },
            { key: "counts", label: "Contents", mono: true, render: (r) => Object.entries(r.counts || {})
                .map(([k, v]) => `${k}=${v}`).join("  ") },
            { key: "action", label: "", align: "right", render: (r) => (
              <Btn size="sm" onClick={() => open(r.id)} testId={`open-pack-${r.id.slice(0, 8)}`}>Open</Btn>) },
          ]}
          rows={packs.map((p) => ({ ...p, __key: p.id }))} />
      </Panel>

      {doc && (
        <Panel title="Pack contents" meta={`sha256 ${doc.sha256}`}
               actions={<Btn size="sm" onClick={download} testId="download-pack">Download JSON</Btn>}
               testId="panel-exam-pack-doc">
          <div className="mrd-grid mrd-grid-3" style={{ gap: 14, marginBottom: 16 }}>
            <Kv label="Trial balance" value={<Badge tone={doc.document.trial_balance.balanced ? "clean" : "critical"}>
              {doc.document.trial_balance.balanced ? "balanced" : "out of balance"}</Badge>} />
            <Kv label="Audit chain" value={<Badge tone={doc.document.audit_chain.ok ? "clean" : "critical"}>
              {doc.document.audit_chain.ok ? "verified" : "broken"}</Badge>} />
            <Kv label="Period" value={`${doc.document.period.start.slice(0, 10)} → ${doc.document.period.end.slice(0, 10)}`} mono />
          </div>
          <p className="mrd-sub" style={{ marginBottom: 14 }}>{doc.document.custody_statement}</p>
          <Code value={doc.document} testId="exam-pack-json" />
        </Panel>
      )}
    </>
  );
}

/* ───────────────────────── vertical packs ───────────────────────── */

function Verticals() {
  const [packs, setPacks] = useState([]);
  useEffect(() => { api.verticals().then((r) => setPacks(r.verticals)); }, []);

  return (
    <>
      <div>
        <h1 className="mrd-h1">Vertical packs</h1>
        <p className="mrd-sub">A vertical is configuration, not code: ledger buckets, obligation kinds, policy rules,
          reconciliation checks, underwriting features and tax reporting. The engine never changes.</p>
      </div>
      {packs.map((p) => (
        <Panel key={p.key} title={p.label} meta={`${p.policy_rule_count} policy rules · ${p.reconciliation.mode.replace("_", "-")} reconciliation`}
               testId={`panel-vertical-${p.key}`}>
          <div className="mrd-grid mrd-grid-3" style={{ gap: 14 }}>
            <Kv label="Ledger buckets" value={p.buckets.join(", ")} mono />
            <Kv label="Account purposes" value={p.account_purposes.join(", ")} mono />
            <Kv label="Obligation kinds" value={p.obligation_kinds.join(", ")} mono />
            <Kv label="Reconciliation checks" value={p.reconciliation.checks.join(", ")} mono />
            <Kv label="Underwriting features" value={p.underwriting_features.join(", ")} mono />
            <Kv label="Tax forms" value={(p.tax_reporting.forms || []).join(", ") || "—"} mono />
            <Kv label="States configured" value={p.states_configured.join(", ") || "—"} mono />
          </div>
        </Panel>
      ))}
    </>
  );
}
